import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';
import { deriveKey, generateSalt, exportKeyToJwk, importKeyFromJwk, generateRecoveryPhrase, hashRecoveryPhrase, encryptContent, decryptContent } from '@/lib/crypto';

const AuthContext = createContext(null);

const BACKUP_REMINDER_DAYS = 7;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [encryptionKey, setEncryptionKey] = useState(null);
  const [encryptionPassword, setEncryptionPassword] = useState(null);
  const [showBackupReminder, setShowBackupReminder] = useState(false);

  useEffect(() => {
    if (token) {
      authAPI.me()
        .then(async (res) => {
          setUser(res.data);
          const storedJwk = localStorage.getItem('enc_key');
          if (storedJwk && res.data.encryption_enabled) {
            try {
              const key = await importKeyFromJwk(JSON.parse(storedJwk));
              setEncryptionKey(key);
            } catch {
              sessionStorage.removeItem('enc_key'); // legacy cleanup
              localStorage.removeItem('enc_key');
            }
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);

    if (userData.encryption_enabled && userData.encryption_salt) {
      const key = await deriveKey(password, userData.encryption_salt);
      // Verify derived key matches stored verification ciphertext
      if (userData.encryption_verify && userData.encryption_verify_iv) {
        try {
          const plain = await decryptContent(userData.encryption_verify, userData.encryption_verify_iv, key);
          if (plain === 'vaultnotes_verify') {
            const jwk = await exportKeyToJwk(key);
            localStorage.setItem('enc_key', JSON.stringify(jwk));
            setEncryptionKey(key);
            setEncryptionPassword(password);
          }
          // If verification fails, vault stays locked — user can unlock manually
        } catch {
          // Key doesn't match — vault stays locked
        }
      } else {
        // No verification data (legacy) — trust the key
        const jwk = await exportKeyToJwk(key);
        localStorage.setItem('enc_key', JSON.stringify(jwk));
        setEncryptionKey(key);
        setEncryptionPassword(password);
      }
    }

    return userData;
  }, []);

  const register = useCallback(async (email, password, accessCode) => {
    const res = await authAPI.register({ email, password, access_code: accessCode });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('enc_key');
    sessionStorage.removeItem('enc_key'); // legacy cleanup
    setToken(null);
    setUser(null);
    setEncryptionKey(null);
    setEncryptionPassword(null);
  }, []);

  const enableEncryption = useCallback(async (password) => {
    const salt = generateSalt();
    const key = await deriveKey(password, salt);
    // Create verification ciphertext for unlock validation
    const verify = await encryptContent('vaultnotes_verify', key);
    await authAPI.updateSettings({
      encryption_enabled: true,
      encryption_salt: salt,
      encryption_verify: verify.content,
      encryption_verify_iv: verify.iv,
    });
    const jwk = await exportKeyToJwk(key);
    localStorage.setItem('enc_key', JSON.stringify(jwk));
    setEncryptionKey(key);
    setEncryptionPassword(password);
    setUser((prev) => ({
      ...prev,
      encryption_enabled: true,
      encryption_salt: salt,
      encryption_verify: verify.content,
      encryption_verify_iv: verify.iv,
    }));
  }, []);

  const disableEncryption = useCallback(async () => {
    await authAPI.updateSettings({
      encryption_enabled: false,
      encryption_salt: '',
      encryption_verify: '',
      encryption_verify_iv: '',
    });
    localStorage.removeItem('enc_key');
    sessionStorage.removeItem('enc_key'); // legacy cleanup
    setEncryptionKey(null);
    setEncryptionPassword(null);
    setUser((prev) => ({
      ...prev,
      encryption_enabled: false,
      encryption_salt: '',
      encryption_verify: '',
      encryption_verify_iv: '',
    }));
  }, []);

  const unlockVault = useCallback(async (password) => {
    if (!user?.encryption_salt) return false;
    try {
      const key = await deriveKey(password, user.encryption_salt);
      // Validate key against stored verification ciphertext
      if (user.encryption_verify && user.encryption_verify_iv) {
        const plain = await decryptContent(user.encryption_verify, user.encryption_verify_iv, key);
        if (plain !== 'vaultnotes_verify') return false;
      }
      const jwk = await exportKeyToJwk(key);
      localStorage.setItem('enc_key', JSON.stringify(jwk));
      setEncryptionKey(key);
      setEncryptionPassword(password);
      return true;
    } catch {
      return false;
    }
  }, [user]);

  // Generate a new recovery key and store its hash
  const generateRecoveryKey = useCallback(async () => {
    const phrase = generateRecoveryPhrase();
    // Normalize phrase before hashing (lowercase, trimmed, single spaces)
    const normalizedPhrase = phrase.toLowerCase().trim().replace(/\s+/g, ' ');
    const hash = await hashRecoveryPhrase(normalizedPhrase);
    await authAPI.updateSettings({ recovery_key_hash: hash });
    setUser((prev) => ({ ...prev, recovery_key_hash: hash }));
    return phrase;
  }, []);

  // Record that a backup was created
  const recordBackup = useCallback(async () => {
    const now = new Date().toISOString();
    await authAPI.updateSettings({ last_backup_at: now });
    setUser((prev) => ({ ...prev, last_backup_at: now }));
    setShowBackupReminder(false);
  }, []);

  // Dismiss backup reminder for now
  const dismissBackupReminder = useCallback(async () => {
    const now = new Date().toISOString();
    await authAPI.updateSettings({ backup_reminder_dismissed_at: now });
    setUser((prev) => ({ ...prev, backup_reminder_dismissed_at: now }));
    setShowBackupReminder(false);
  }, []);

  // Check if backup reminder should be shown
  useEffect(() => {
    if (!user) {
      setShowBackupReminder(false);
      return;
    }

    const lastBackup = user.last_backup_at ? new Date(user.last_backup_at) : null;
    const dismissed = user.backup_reminder_dismissed_at ? new Date(user.backup_reminder_dismissed_at) : null;
    const now = new Date();
    const reminderDays = BACKUP_REMINDER_DAYS * 24 * 60 * 60 * 1000;

    // Show reminder if:
    // 1. Never backed up, OR
    // 2. Last backup was more than 7 days ago AND (never dismissed OR dismissed more than 7 days ago)
    const shouldRemind = !lastBackup || (now - lastBackup > reminderDays);
    const notRecentlyDismissed = !dismissed || (now - dismissed > reminderDays);

    setShowBackupReminder(shouldRemind && notRecentlyDismissed);
  }, [user]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data);
    } catch {
      // ignore
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        encryptionKey,
        encryptionPassword,
        login,
        register,
        logout,
        enableEncryption,
        disableEncryption,
        unlockVault,
        isEncryptionEnabled: user?.encryption_enabled || false,
        isVaultLocked: !!(user?.encryption_enabled && !encryptionKey),
        generateRecoveryKey,
        recordBackup,
        dismissBackupReminder,
        showBackupReminder,
        hasRecoveryKey: !!user?.recovery_key_hash,
        lastBackupAt: user?.last_backup_at || null,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
